// client/src/components/indicadores/FilterBar.js
import React from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useAuth } from '../../context/auth/AuthProvider';
import { Search, XCircle as ClearIcon } from 'lucide-react';
import { ptBR, enUS } from 'date-fns/locale';

// ✅ CORREÇÃO: Registrar locales corretamente
registerLocale('pt-BR', ptBR);
registerLocale('en-US', enUS);

const FilterBar = ({ filters, setFilters, onApply, onClear, options, enabledFilters, isLoading }) => {
    const { user } = useAuth();

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (onApply) {
            onApply();
        }
    };

    const handleClear = () => {
        if (onClear) {
            onClear();
        }
    };
    
    const isAnyFilterActive = () => {
        return Object.entries(filters).some(([key, value]) => {
            if (key === 'startDate' || key === 'endDate') return false;
            return value !== '';
        });
    };

    // ✅ CORREÇÃO COMPLETA: Função segura para obter locale
    const getDatePickerLocale = () => {
        try {
            // Tentar obter locale das configurações da conta
            const accountLocale = user?.account?.configuracoesGerais?.locale;
            
            // Se não tiver locale na conta, usar fallback baseado no navegador
            if (!accountLocale) {
                const browserLang = navigator.language || 'pt-BR';
                return browserLang.startsWith('en') ? 'en-US' : 'pt-BR';
            }
            
            // Mapear possíveis valores para os locales suportados
            const localeMap = {
                'pt': 'pt-BR',
                'pt-BR': 'pt-BR',
                'pt-PT': 'pt-BR', // Usar pt-BR como fallback para português
                'en': 'en-US',
                'en-US': 'en-US',
                'en-GB': 'en-US', // Usar en-US como fallback para inglês
            };
            
            return localeMap[accountLocale] || 'pt-BR';
        } catch (error) {
            console.warn('❌ Erro ao obter locale, usando pt-BR como fallback:', error);
            return 'pt-BR';
        }
    };

    const datePickerLocale = getDatePickerLocale();

    return (
        <form onSubmit={handleSubmit}>
            <div className="form-grid-container">
                {enabledFilters.includes('date') && (
                    <>
                        <div className="form-group">
                            <label>Período (Início)</label>
                            <DatePicker 
                                selected={filters.startDate} 
                                onChange={(date) => handleFilterChange('startDate', date)} 
                                locale={datePickerLocale} 
                                dateFormat="dd/MM/yyyy" 
                                className="form-input" 
                                selectsStart
                                startDate={filters.startDate}
                                endDate={filters.endDate}
                                isClearable
                                placeholderText="Selecione a data inicial"
                                disabled={isLoading}
                            />
                        </div>
                        <div className="form-group">
                            <label>Período (Fim)</label>
                            <DatePicker 
                                selected={filters.endDate} 
                                onChange={(date) => handleFilterChange('endDate', date)} 
                                locale={datePickerLocale} 
                                dateFormat="dd/MM/yyyy" 
                                className="form-input" 
                                selectsEnd
                                startDate={filters.startDate}
                                endDate={filters.endDate}
                                minDate={filters.startDate}
                                isClearable
                                placeholderText="Selecione a data final"
                                disabled={isLoading}
                            />
                        </div>
                    </>
                )}
                
                {enabledFilters.includes('unidade') && (
                    <div className="form-group">
                        <label>Unidade</label>
                        <select 
                            className="form-input" 
                            value={filters.unidadeId} 
                            onChange={e => handleFilterChange('unidadeId', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todas</option>
                            {options.unidades?.map(unidade => (
                                <option key={unidade._id} value={unidade._id}>
                                    {unidade.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                
                {enabledFilters.includes('profissional') && (
                    <div className="form-group">
                        <label>Profissional</label>
                        <select 
                            className="form-input" 
                            value={filters.profissionalId} 
                            onChange={e => handleFilterChange('profissionalId', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todos</option>
                            {options.profissionais?.map(profissional => (
                                <option key={profissional._id} value={profissional._id}>
                                    {profissional.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                
                {enabledFilters.includes('servico') && (
                    <div className="form-group">
                        <label>Serviço</label>
                        <select 
                            className="form-input" 
                            value={filters.servicoId} 
                            onChange={e => handleFilterChange('servicoId', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todos</option>
                            {options.servicos?.map(servico => (
                                <option key={servico._id} value={servico._id}>
                                    {servico.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                
                {enabledFilters.includes('recurso') && (
                    <div className="form-group">
                        <label>Recurso</label>
                        <select 
                            className="form-input" 
                            value={filters.recursoId} 
                            onChange={e => handleFilterChange('recursoId', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todos</option>
                            {options.recursos?.map(recurso => (
                                <option key={recurso._id} value={recurso._id}>
                                    {recurso.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                
                {enabledFilters.includes('produto') && (
                    <div className="form-group">
                        <label>Produto</label>
                        <select
                            className="form-input"
                            value={filters.produtoId}
                            onChange={e => handleFilterChange('produtoId', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todos</option>
                            {options.produtos?.map(produto => (
                                <option key={produto._id} value={produto._id}>
                                    {produto.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {enabledFilters.includes('metodoPagamento') && (
                    <div className="form-group">
                        <label>Forma de Pagamento</label>
                        <select 
                            className="form-input" 
                            name="metodoPagamentoId" 
                            value={filters.metodoPagamentoId} 
                            onChange={e => handleFilterChange('metodoPagamentoId', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todas</option>
                            {options.metodosPagamento?.map(metodo => (
                                <option key={metodo._id} value={metodo._id}>
                                    {metodo.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                
                {enabledFilters.includes('comAgendamento') && (
                    <div className="form-group">
                        <label>Com Agendamento no Período?</label>
                        <select 
                            className="form-input" 
                            name="comAgendamento" 
                            value={filters.comAgendamento} 
                            onChange={e => handleFilterChange('comAgendamento', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todos</option>
                            <option value="sim">Sim</option>
                            <option value="nao">Não</option>
                        </select>
                    </div>
                )}
                
                {enabledFilters.includes('origem') && (
                    <div className="form-group">
                        <label>Origem do Agendamento</label>
                        <select 
                            className="form-input" 
                            value={filters.origemAgendamento} 
                            onChange={e => handleFilterChange('origemAgendamento', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todas</option>
                            {options.origens?.map(origem => (
                                <option key={origem.value} value={origem.value}>
                                    {origem.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                
                {enabledFilters.includes('tipo') && (
                    <div className="form-group">
                        <label>Tipo de Agendamento</label>
                        <select 
                            className="form-input" 
                            value={filters.tipoAgendamento} 
                            onChange={e => handleFilterChange('tipoAgendamento', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todos</option>
                            {options.tipos?.map(tipo => (
                                <option key={tipo.value} value={tipo.value}>
                                    {tipo.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                
                {enabledFilters.includes('plataforma') && (
                    <div className="form-group">
                        <label>Plataforma</label>
                        <select 
                            className="form-input" 
                            value={filters.plataforma} 
                            onChange={e => handleFilterChange('plataforma', e.target.value)}
                            disabled={isLoading}
                        >
                            <option value="">Todas</option>
                            {options.plataformas?.map(plataforma => (
                                <option key={plataforma.value} value={plataforma.value}>
                                    {plataforma.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
            
            <div className="form-actions">
                <button 
                    type="button" 
                    onClick={handleClear} 
                    className="action-button action-button-secondary" 
                    disabled={!isAnyFilterActive() || isLoading}
                >
                    <ClearIcon size={18} className="icon"/> 
                    Limpar Filtros
                </button>
                <button 
                    type="submit" 
                    className="action-button action-button-primary" 
                    disabled={isLoading}
                >
                    <Search size={18} className="icon"/> 
                    Aplicar Filtros
                </button>
            </div>
        </form>
    );
};

export default FilterBar;